<template>
    <div>
        <form-modal
            ref="formModal"
            :title="modalTitle"
            size="xl"
            :in-editing="isEdit"
            :handle-close="handleClose"
        >
            <template v-slot:form>
                <div
                    v-if="campaignHasId"
                    class="loja-perfeita-modal-toolbar d-flex justify-content-end align-items-center flex-nowrap w-100 mb-2"
                >
                    <edit-label-button
                        v-if="!isEdit"
                        :title="getI18n(i18nKey, 'TITLES.dados_gerais')"
                        :disabled="submitLoading"
                        @onClick="enterCampaignEditMode"
                    />
                </div>
                <b-tabs
                    v-model="tabIndex"
                    class="tabs-pdv"
                >
                    <b-tab :title="getI18n(i18nKey, 'TITLES.dados_gerais')">
                        <p class="text-muted mb-3">
                            {{ getI18n(i18nKey, 'INTROS.config_campanha_dados') }}
                        </p>
                        <form @submit.prevent="handleSubmitCampaign">
                            <b-row>
                                <b-col md="12">
                                    <b-form-group
                                        :label="getI18n(i18nKey, 'TITLES.nome')"
                                        label-for="setup-campaign-name"
                                        class="required label-pdv"
                                    >
                                        <b-form-input
                                            id="setup-campaign-name"
                                            v-model.trim="formData.name"
                                            class="input-pdv-blue"
                                            type="text"
                                            :placeholder="getI18n(i18nKey, 'TITLES.nome')"
                                            :disabled="submitLoading || !isFormEditable"
                                        />
                                    </b-form-group>
                                </b-col>
                            </b-row>
                            <b-row>
                                <b-col md="6">
                                    <b-form-group
                                        :label="getI18n(i18nKey, 'TITLES.inicio')"
                                        label-for="setup-campaign-start"
                                        class="label-pdv"
                                    >
                                        <b-form-input
                                            id="setup-campaign-start"
                                            v-model="formData.start_date"
                                            class="input-pdv-blue"
                                            type="date"
                                            :disabled="submitLoading || !isFormEditable"
                                        />
                                    </b-form-group>
                                </b-col>
                                <b-col md="6">
                                    <b-form-group
                                        :label="getI18n(i18nKey, 'TITLES.fim')"
                                        label-for="setup-campaign-end"
                                        class="label-pdv"
                                    >
                                        <b-form-input
                                            id="setup-campaign-end"
                                            v-model="formData.end_date"
                                            class="input-pdv-blue"
                                            type="date"
                                            :disabled="submitLoading || !isFormEditable"
                                        />
                                    </b-form-group>
                                </b-col>
                            </b-row>
                            <b-row>
                                <b-col md="12">
                                    <b-form-group class="label-pdv">
                                        <b-form-checkbox
                                            id="setup-campaign-active"
                                            v-model="formData.is_active"
                                            :disabled="submitLoading || !isFormEditable"
                                        >
                                            {{ getI18n(i18nKey, 'TITLES.ativa') }}
                                        </b-form-checkbox>
                                    </b-form-group>
                                </b-col>
                            </b-row>
                            <b-alert
                                v-if="!campaignHasId"
                                variant="light"
                                show
                                class="mb-0 mt-2"
                            >
                                {{ getI18n(i18nKey, 'MESSAGES.complete_campaign_dados_tab') }}
                            </b-alert>
                            <div class="d-flex justify-content-end gap-2 mt-3">
                                <b-button
                                    class="btn btn-pdv-gradient-blue-transparent"
                                    type="button"
                                    :disabled="submitLoading"
                                    @click="handleClose"
                                >
                                    {{ getI18n(i18nKey, 'BUTTONS.cancelar') }}
                                </b-button>
                                <b-button
                                    class="btn btn-pdv-gradient-blue ml-3"
                                    type="submit"
                                    :disabled="submitLoading || !formData.name || !isFormEditable"
                                >
                                    <b-spinner v-if="submitLoading" small class="mr-1" />
                                    {{ getI18n(i18nKey, 'BUTTONS.salvar') }}
                                </b-button>
                            </div>
                        </form>
                    </b-tab>
                    <b-tab
                        :title="getI18n(i18nKey, 'TITLES.pilares')"
                        :disabled="!campaignHasId"
                    >
                        <div class="mt-2">
                            <div class="d-flex justify-content-between align-items-center mb-4">
                                <p class="text-muted mb-0">
                                    {{ getI18n(i18nKey, 'INTROS.pilares') }}
                                </p>
                                <b-button
                                    v-if="campaignHasId"
                                    size="sm"
                                    class="btn-pdv-gradient-blue"
                                    @click="openPillarModal()"
                                >
                                    <i class="fe-plus-circle" aria-hidden="true"></i>
                                    {{ getI18n(i18nKey, 'BUTTONS.adicionar_pilar') }}
                                </b-button>
                            </div>
                            <loja-perfeita-list-skeleton
                                v-if="pillarsLoading"
                                :rows="5"
                                :columns="3"
                            />
                            <b-table
                                v-else
                                :items="pillars"
                                :fields="pillarFields"
                                striped
                                responsive
                                show-empty
                                :empty-text="getI18n(i18nKey, 'MESSAGES.empty_pilares')"
                            >
                                <template v-slot:cell(actions)="{ item }">
                                    <div class="table-button-container text-right">
                                        <more-table-button @onClick="openPillarModal(item)" />
                                    </div>
                                </template>
                            </b-table>
                        </div>
                    </b-tab>
                    <b-tab
                        :title="getI18n(i18nKey, 'TITLES.segmentacao')"
                        :disabled="!campaignHasId"
                    >
                        <div class="mt-2">
                            <loja-perfeita-conditions-editor
                                v-if="campaignHasId"
                                ref="conditionsEditor"
                                parent-type="campaign"
                                :parent-id="formData.id"
                                :hint="getI18n(i18nKey, 'MESSAGES.segmentacao_hint_campanha')"
                            />
                        </div>
                    </b-tab>
                    <b-tab
                        :title="getI18n(i18nKey, 'TITLES.faixas_desempenho')"
                        :disabled="!campaignHasId"
                    >
                        <div class="mt-2">
                            <div class="d-flex justify-content-between align-items-center mb-4">
                                <p class="text-muted mb-0">
                                    {{ getI18n(i18nKey, 'INTROS.faixas') }}
                                </p>
                                <b-button
                                    v-if="campaignHasId"
                                    size="sm"
                                    class="btn-pdv-gradient-blue"
                                    @click="openBandModal()"
                                >
                                    <i class="fe-plus-circle" aria-hidden="true"></i>
                                    {{ getI18n(i18nKey, 'BUTTONS.adicionar_faixa') }}
                                </b-button>
                            </div>
                            <loja-perfeita-list-skeleton
                                v-if="bandsLoading"
                                :rows="5"
                                :columns="5"
                            />
                            <b-table
                                v-else
                                :items="performanceBands"
                                :fields="bandFields"
                                striped
                                responsive
                                show-empty
                                :empty-text="getI18n(i18nKey, 'MESSAGES.empty_faixas')"
                            >
                                <template v-slot:cell(color)="{ item }">
                                    <span
                                        class="d-inline-block rounded mr-1"
                                        style="width: 20px; height: 20px; background: var(--band-color)"
                                        :style="{ '--band-color': item.color || '#6c757d' }"
                                    ></span>
                                    {{ item.color }}
                                </template>
                                <template v-slot:cell(actions)="{ item }">
                                    <div class="table-button-container text-right">
                                        <more-table-button @onClick="openBandModal(item)" />
                                    </div>
                                </template>
                            </b-table>
                        </div>
                    </b-tab>
                </b-tabs>
            </template>
        </form-modal>

        <loja-perfeita-pillar-modal
            v-if="campaignHasId"
            ref="pillarModal"
            :campaign-id="formData.id"
            @refresh="loadPillars"
        />
        <loja-perfeita-band-modal
            v-if="campaignHasId"
            ref="bandModal"
            :campaign-id="formData.id"
            @refresh="loadBands"
        />

    </div>
</template>

<script>
import FormModal from '@src/components/FormModal.vue'
import EditLabelButton from '@/src/components/EditLabelButton.vue'
import LojaPerfeitaService from '@src/services/dashboards/LojaPerfeitaService'
import MoreTableButton from '@/src/components/MoreTableButton.vue'
import LojaPerfeitaPillarModal from './lojaPerfeitaPillarModal.vue'
import LojaPerfeitaBandModal from './lojaPerfeitaBandModal.vue'
import LojaPerfeitaListSkeleton from '@src/components/dashboards/lojaPerfeita/LojaPerfeitaListSkeleton.vue'
import LojaPerfeitaConditionsEditor from '@src/components/dashboards/lojaPerfeita/LojaPerfeitaConditionsEditor.vue'
import Shared from '@src/mixins/shared.vue'
import swalFeedback from '@src/mixins/swalFeedback'

const i18nKey = 'LOJA_PERFEITA'

const defaultForm = () => ({
    id: 0,
    name: '',
    start_date: '',
    end_date: '',
    is_active: true,
})

/** API returns ISO datetimes; <input type="date"> only accepts YYYY-MM-DD. */
function toDateInputValue(value) {
    if (value == null || value === '') {
        return ''
    }
    if (typeof value === 'string') {
        const m = value.match(/^(\d{4}-\d{2}-\d{2})/)
        return m ? m[1] : ''
    }
    if (value instanceof Date && !Number.isNaN(value.getTime())) {
        const y = value.getFullYear()
        const mo = String(value.getMonth() + 1).padStart(2, '0')
        const d = String(value.getDate()).padStart(2, '0')
        return `${y}-${mo}-${d}`
    }
    return ''
}

export default {
    components: {
        FormModal,
        EditLabelButton,
        MoreTableButton,
        LojaPerfeitaPillarModal,
        LojaPerfeitaBandModal,
        LojaPerfeitaListSkeleton,
        LojaPerfeitaConditionsEditor,
    },
    mixins: [Shared, swalFeedback],
    data() {
        return {
            i18nKey,
            isEdit: false,
            submitLoading: false,
            tabIndex: 0,
            formData: defaultForm(),
            pillars: [],
            performanceBands: [],
            pillarsLoading: false,
            bandsLoading: false,
            pillarFields: [
                { key: 'name', label: this.getI18n(i18nKey, 'TITLES.nome') },
                { key: 'weight', label: this.getI18n(i18nKey, 'TITLES.pontuacao_pilar') },
                { key: 'actions', label: this.getI18n(i18nKey, 'TITLES.acoes') },
            ],
            bandFields: [
                { key: 'label', label: this.getI18n(i18nKey, 'TITLES.faixa') },
                { key: 'min_score', label: this.getI18n(i18nKey, 'FIELDS.score_min') },
                { key: 'max_score', label: this.getI18n(i18nKey, 'FIELDS.score_max') },
                { key: 'color', label: this.getI18n(i18nKey, 'FIELDS.color') },
                { key: 'actions', label: this.getI18n(i18nKey, 'TITLES.acoes') },
            ],
        }
    },
    computed: {
        campaignHasId() {
            return Boolean(this.formData.id && Number(this.formData.id) > 0)
        },
        modalTitle() {
            if (!this.formData.id || Number(this.formData.id) === 0) {
                return this.getI18n(i18nKey, 'TITLES.nova_campanha')
            }
            return this.getI18n(i18nKey, 'TITLES.configurar_campanha')
        },
        isFormEditable() {
            return !this.campaignHasId || this.isEdit
        },
    },
    watch: {
        tabIndex(val) {
            if (!this.campaignHasId) return
            if (val === 1) this.loadPillars()
            if (val === 2) this.loadConditions()
            if (val === 3) this.loadBands()
        },
    },
    methods: {
        normalizeCampaignDateFields(payload) {
            if (!payload || typeof payload !== 'object') {
                return payload
            }
            return {
                ...payload,
                start_date: toDateInputValue(payload.start_date),
                end_date: toDateInputValue(payload.end_date),
            }
        },
        enterCampaignEditMode() {
            this.isEdit = true
            this.tabIndex = 0
            this.$nextTick(() => {
                const el = document.getElementById('setup-campaign-name')
                if (el) el.focus()
            })
        },
        showModal() {
            this.submitLoading = false
            this.tabIndex = 0
            this.$refs.formModal.show()
            this.$nextTick(() => {
                if (this.campaignHasId) {
                    this.loadPillars()
                    this.loadBands()
                }
            })
        },
        replaceModalData(data) {
            const normalized = this.normalizeCampaignDateFields(data)
            this.formData = { ...defaultForm(), ...normalized }
            this.tabIndex = 0
            this.isEdit = !(this.formData.id && Number(this.formData.id) > 0)
            this.pillars = []
            this.performanceBands = []
        },
        handleClose() {
            this.$refs.formModal.hide()
        },
        loadPillars() {
            if (!this.campaignHasId) return
            this.pillarsLoading = true
            LojaPerfeitaService.getPillars(this.formData.id)
                .then((res) => {
                    this.pillars = res.data || []
                })
                .catch(() => {
                    this.pillars = []
                })
                .finally(() => {
                    this.pillarsLoading = false
                })
        },
        loadConditions() {
            if (!this.campaignHasId) return
            if (this.$refs.conditionsEditor) {
                this.$refs.conditionsEditor.loadConditions()
                this.$refs.conditionsEditor.loadDimensions()
            }
        },
        loadBands() {
            if (!this.campaignHasId) return
            this.bandsLoading = true
            LojaPerfeitaService.getPerformanceBands(this.formData.id)
                .then((res) => {
                    this.performanceBands = res.data || []
                })
                .catch(() => {
                    this.performanceBands = []
                })
                .finally(() => {
                    this.bandsLoading = false
                })
        },
        handleSubmitCampaign() {
            if (!this.formData.name || this.submitLoading || !this.isFormEditable) return
            this.submitLoading = true
            const payload = {
                name: this.formData.name,
                start_date: this.formData.start_date || null,
                end_date: this.formData.end_date || null,
                is_active: this.formData.is_active,
            }
            const wasNew = !this.campaignHasId
            const promise = this.formData.id
                ? LojaPerfeitaService.update(this.formData.id, payload)
                : LojaPerfeitaService.create(payload)
            promise
                .then((res) => {
                    const saved = res.data
                    if (saved && saved.id != null) {
                        this.formData = this.normalizeCampaignDateFields({
                            ...this.formData,
                            ...saved,
                        })
                    }
                    this.successFeedback(
                        this.getI18n(i18nKey, 'TITLES.loja_perfeita'),
                        this.getI18n(i18nKey, 'MESSAGES.campanha_salva')
                    )
                    this.isEdit = false
                    this.$emit('refresh')
                    if (wasNew && this.campaignHasId) {
                        this.loadPillars()
                        this.loadBands()
                        this.tabIndex = 1
                    }
                })
                .catch((err) => {
                    this.negativeFeedback(
                        this.getI18n('RESPONSES', 'something_wrong'),
                        err?.response?.data?.message || err?.message || this.getI18n('RESPONSES', 'operation_not_possible')
                    )
                })
                .finally(() => {
                    this.submitLoading = false
                })
        },
        openPillarModal(pillar = null) {
            this.$refs.pillarModal.replaceModalData(pillar ? { ...pillar } : { id: 0, name: '', weight: 0 })
            this.$nextTick(() => this.$refs.pillarModal.showModal())
        },
        openBandModal(band = null) {
            this.$refs.bandModal.replaceModalData(
                band ? { ...band } : { id: 0, label: '', min_score: 0, max_score: 100, color: '#6c757d' }
            )
            this.$nextTick(() => this.$refs.bandModal.showModal())
        },
    },
}
</script>

<style scoped>
.loja-perfeita-modal-toolbar {
    gap: 0.5rem;
    overflow-x: auto;
}
</style>
